import { Graphics } from 'pixi.js';
import { PLAYER } from '../domain/content.js';
import type { ViewModel } from '../contract.js';

const LINE = 0xf7efd7;
const SHADOW = 0x111725;
const SUIT = 0x55d2b2;
const SUIT_DARK = 0x267a6f;
const SCARF = 0xf0c36a;
const SCARF_SHADOW = 0xb98536;
const SOLE = 0x1a2230;

interface PosePoint {
  x: number;
  y: number;
}

interface FigurePose {
  hip: PosePoint;
  chest: PosePoint;
  neck: PosePoint;
  head: PosePoint;
  leftKnee: PosePoint;
  leftFoot: PosePoint;
  rightKnee: PosePoint;
  rightFoot: PosePoint;
  leftElbow: PosePoint;
  leftHand: PosePoint;
  rightElbow: PosePoint;
  rightHand: PosePoint;
  bob: number;
  lean: number;
}

export function drawStickFigure(graphics: Graphics, view: ViewModel): void {
  const pose = buildPose(view);
  const player = view.player;
  const facing = player.facing;

  graphics.ellipse(player.x - player.upX * 2, player.y - player.upY * 2, 18, 5).fill({ color: SHADOW, alpha: 0.18 });

  drawLimb(graphics, pose.hip, pose.leftKnee, pose.leftFoot, LINE, 4.1);
  drawLimb(graphics, pose.hip, pose.rightKnee, pose.rightFoot, LINE, 4.1);
  drawFoot(graphics, view, pose.leftFoot, facing, player.motionState === 'fall' ? -1 : 1);
  drawFoot(graphics, view, pose.rightFoot, facing, player.motionState === 'jump' ? 1 : -1);
  drawJoint(graphics, pose.leftKnee, 2.4);
  drawJoint(graphics, pose.rightKnee, 2.4);

  graphics
    .moveTo(pose.hip.x, pose.hip.y)
    .lineTo(pose.chest.x, pose.chest.y)
    .lineTo(pose.neck.x, pose.neck.y)
    .stroke({ color: LINE, width: 5.1, alpha: 0.96, cap: 'round', join: 'round' });
  graphics.circle(pose.chest.x, pose.chest.y, 5.4).fill({ color: SUIT, alpha: 0.95 });
  graphics.circle(pose.hip.x, pose.hip.y, 4.7).fill({ color: SUIT_DARK, alpha: 0.95 });

  drawLimb(graphics, pose.chest, pose.leftElbow, pose.leftHand, LINE, 3.3);
  drawLimb(graphics, pose.chest, pose.rightElbow, pose.rightHand, LINE, 3.3);
  drawJoint(graphics, pose.leftElbow, 2.1);
  drawJoint(graphics, pose.rightElbow, 2.1);
  graphics.circle(pose.leftHand.x, pose.leftHand.y, 2.5).fill({ color: LINE, alpha: 0.95 });
  graphics.circle(pose.rightHand.x, pose.rightHand.y, 2.5).fill({ color: LINE, alpha: 0.95 });

  drawScarf(graphics, view, pose);
  graphics.circle(pose.head.x, pose.head.y, PLAYER.headRadius).fill({ color: LINE, alpha: 0.98 });
  const face = local(view, pose.lean + facing * 8.2, 91 - pose.bob - player.landing01 * 2.4);
  graphics.circle(face.x, face.y, 2.1).fill({ color: SHADOW, alpha: 0.95 });
}

function buildPose(view: ViewModel): FigurePose {
  const player = view.player;
  const speed01 = Math.min(1, Math.abs(player.tangentVelocity) / PLAYER.maxGroundSpeed);
  const cycle = player.walkCycle * Math.PI * 2;
  const facing = player.facing;
  const land = player.landing01;

  if (player.motionState === 'jump') return airbornePose(view, 1);
  if (player.motionState === 'fall') return airbornePose(view, -1);
  if (player.motionState === 'land') return landingPose(view);
  if (player.motionState === 'swim') return swimPose(view);
  if (player.motionState === 'blocked') return blockedPose(view);
  if (player.motionState === 'idle-long') return longIdlePose(view);

  const idle01 = player.motionState === 'idle' ? 1 : 0;
  const walk01 = player.motionState === 'walk' ? 1 : 0;
  const run01 = player.motionState === 'run' ? 1 : 0;
  const stride = walk01 * 13 + run01 * 20;
  const lift = walk01 * 4.5 + run01 * 8;
  const lean = facing * (idle01 * 0.7 + walk01 * 2.5 + run01 * 6.5);
  const breath = Math.sin(view.tick * 0.045) * 1.1;
  const stepBob = Math.max(0, Math.sin(cycle + Math.PI * 0.5)) * (walk01 * 1.8 + run01 * 3.4);
  const bob = idle01 * breath + stepBob;

  const left = Math.sin(cycle);
  const right = Math.sin(cycle + Math.PI);
  const leftLift = Math.max(0, Math.cos(cycle)) * lift;
  const rightLift = Math.max(0, Math.cos(cycle + Math.PI)) * lift;

  return {
    hip: local(view, lean * 0.32, 35 - bob + land * 1.8),
    chest: local(view, lean, 66 - bob - land),
    neck: local(view, lean + facing * 1.5, 78 - bob - land * 1.8),
    head: local(view, lean + facing * 2.5, 90 - bob - land * 2.5),
    leftKnee: local(view, left * stride * 0.44 - 4, 19 + leftLift * 0.36 - bob * 0.1),
    leftFoot: local(view, left * stride, PLAYER.footRadius + leftLift),
    rightKnee: local(view, right * stride * 0.44 + 4, 19 + rightLift * 0.36 - bob * 0.1),
    rightFoot: local(view, right * stride, PLAYER.footRadius + rightLift),
    leftElbow: local(view, -left * (walk01 * 7 + run01 * 12) - 7, 53 - bob + Math.max(0, left) * 2),
    leftHand: local(view, -left * (walk01 * 13 + run01 * 19) - 11, 41 - bob - Math.max(0, -left) * 3),
    rightElbow: local(view, -right * (walk01 * 7 + run01 * 12) + 7, 53 - bob + Math.max(0, right) * 2),
    rightHand: local(view, -right * (walk01 * 13 + run01 * 19) + 11, 41 - bob - Math.max(0, -right) * 3),
    bob,
    lean,
  };
}

function longIdlePose(view: ViewModel): FigurePose {
  const player = view.player;
  const facing = player.facing;
  const wave = Math.sin(view.tick * 0.035);
  const glance = Math.sin(view.tick * 0.013) > 0 ? 1 : -1;
  const lean = facing * 0.8 + glance * 1.5;
  const bob = wave * 1.2;

  return {
    hip: local(view, 0, 35 - bob),
    chest: local(view, lean, 65 - bob),
    neck: local(view, lean + glance * 1.2, 78 - bob),
    head: local(view, lean + glance * 3.5, 90 - bob),
    leftKnee: local(view, -5, 19),
    leftFoot: local(view, -8, PLAYER.footRadius),
    rightKnee: local(view, 8, 18),
    rightFoot: local(view, 12, PLAYER.footRadius),
    leftElbow: local(view, -10, 51 - bob),
    leftHand: local(view, -15, 38 - bob + wave * 2),
    rightElbow: local(view, 9, 54 - bob),
    rightHand: local(view, 14, 42 - bob - wave * 2),
    bob,
    lean,
  };
}

function blockedPose(view: ViewModel): FigurePose {
  const player = view.player;
  const facing = player.facing;
  const lean = -facing * 4;
  return {
    hip: local(view, 0, 33),
    chest: local(view, lean, 62),
    neck: local(view, lean, 74),
    head: local(view, lean - facing * 3, 86),
    leftKnee: local(view, -10, 16),
    leftFoot: local(view, -18, PLAYER.footRadius),
    rightKnee: local(view, 8, 17),
    rightFoot: local(view, 13, PLAYER.footRadius),
    leftElbow: local(view, -facing * 12, 57),
    leftHand: local(view, facing * 22, 52),
    rightElbow: local(view, -facing * 10, 45),
    rightHand: local(view, facing * 22, 43),
    bob: 2,
    lean,
  };
}

function swimPose(view: ViewModel): FigurePose {
  const player = view.player;
  const facing = player.facing;
  const cycle = view.tick * 0.11;
  const kick = Math.sin(cycle);
  const reach = Math.sin(cycle + Math.PI * 0.5);
  const lean = facing * 8;

  return {
    hip: local(view, -facing * 4, 22 + Math.sin(cycle * 0.5) * 2),
    chest: local(view, lean * 0.5, 47),
    neck: local(view, lean * 0.75, 60),
    head: local(view, lean, 72),
    leftKnee: local(view, -facing * 18, 18 + kick * 7),
    leftFoot: local(view, -facing * 30, 8 + kick * 10),
    rightKnee: local(view, -facing * 15, 18 - kick * 7),
    rightFoot: local(view, -facing * 28, 8 - kick * 10),
    leftElbow: local(view, facing * (8 + reach * 16), 48 + Math.max(0, reach) * 8),
    leftHand: local(view, facing * (20 + reach * 21), 43 + Math.max(0, reach) * 11),
    rightElbow: local(view, facing * (8 - reach * 16), 48 + Math.max(0, -reach) * 8),
    rightHand: local(view, facing * (20 - reach * 21), 43 + Math.max(0, -reach) * 11),
    bob: 0,
    lean,
  };
}

function airbornePose(view: ViewModel, rising: 1 | -1): FigurePose {
  const player = view.player;
  const facing = player.facing;
  const speedLean = facing * Math.min(7, Math.abs(player.tangentVelocity) * 0.025);
  const float = rising > 0 ? -5 : -1;
  const armLift = rising > 0 ? 9 : -2;
  const legTuck = rising > 0 ? 9 : -3;

  return {
    hip: local(view, speedLean * 0.45, 35 + float),
    chest: local(view, speedLean, 66 + float),
    neck: local(view, speedLean + facing * 1.2, 78 + float),
    head: local(view, speedLean + facing * 2.2, 90 + float),
    leftKnee: local(view, -11 + speedLean * 0.35, 22 + legTuck),
    leftFoot: local(view, -16 + speedLean * 0.25, 8 + legTuck * 0.45),
    rightKnee: local(view, 11 + speedLean * 0.35, 24 - legTuck * 0.35),
    rightFoot: local(view, 16 + speedLean * 0.25, 9 - legTuck * 0.2),
    leftElbow: local(view, -13 + speedLean * 0.25, 57 + armLift),
    leftHand: local(view, -18 + speedLean * 0.15, 45 + armLift * 1.4),
    rightElbow: local(view, 13 + speedLean * 0.25, 57 + armLift),
    rightHand: local(view, 18 + speedLean * 0.15, 45 + armLift * 1.4),
    bob: -float,
    lean: speedLean,
  };
}

function landingPose(view: ViewModel): FigurePose {
  const player = view.player;
  const facing = player.facing;
  const t = player.landing01;
  const crouch = 9 * t;
  const lean = facing * 2;

  return {
    hip: local(view, 0, 28 - crouch * 0.15),
    chest: local(view, lean, 58 - crouch),
    neck: local(view, lean + facing, 70 - crouch),
    head: local(view, lean + facing * 2, 82 - crouch),
    leftKnee: local(view, -9, 15),
    leftFoot: local(view, -17, PLAYER.footRadius),
    rightKnee: local(view, 9, 15),
    rightFoot: local(view, 17, PLAYER.footRadius),
    leftElbow: local(view, -14, 46 - crouch),
    leftHand: local(view, -22, 34 - crouch * 0.5),
    rightElbow: local(view, 14, 46 - crouch),
    rightHand: local(view, 22, 34 - crouch * 0.5),
    bob: crouch,
    lean,
  };
}

function drawScarf(graphics: Graphics, view: ViewModel, pose: FigurePose): void {
  const player = view.player;
  const facing = player.facing;
  const velocity = player.tangentVelocity;
  const speed01 = Math.min(1, Math.abs(velocity) / PLAYER.maxGroundSpeed);
  const walkingTrail = Math.abs(velocity) > 12 ? -Math.sign(velocity) : -facing * 0.35;
  const wind = walkingTrail * (10 + speed01 * 28);
  const idleDrop = (1 - speed01) * 12;
  const fallLift = player.motionState === 'fall' ? 7 : 0;
  const jumpLift = player.motionState === 'jump' ? -4 : 0;
  const flutterA = Math.sin(view.tick * 0.055 + player.walkCycle * Math.PI * 2) * (1.2 + speed01 * 3.4);
  const flutterB = Math.sin(view.tick * 0.037 + player.walkCycle * Math.PI * 2 + 1.7) * (1.8 + speed01 * 4.6);

  const a = local(view, pose.lean - facing * 1.5, 75 - pose.bob);
  const b = local(view, pose.lean + wind * 0.38, 72 - pose.bob - idleDrop * 0.34 + fallLift + jumpLift + flutterA);
  const c = local(view, pose.lean + wind * 0.74, 67 - pose.bob - idleDrop * 0.72 + fallLift * 0.7 + jumpLift + flutterB);
  const d = local(view, pose.lean + wind, 60 - pose.bob - idleDrop + fallLift * 0.45 + jumpLift - flutterA * 0.4);

  graphics
    .moveTo(a.x, a.y)
    .lineTo(b.x, b.y)
    .lineTo(c.x, c.y)
    .lineTo(d.x, d.y)
    .stroke({ color: SCARF_SHADOW, width: 5.1, alpha: 0.42, cap: 'round', join: 'round' });
  graphics
    .moveTo(a.x, a.y)
    .lineTo(b.x, b.y)
    .lineTo(c.x, c.y)
    .lineTo(d.x, d.y)
    .stroke({ color: SCARF, width: 3.2, alpha: 0.94, cap: 'round', join: 'round' });
  graphics.circle(d.x, d.y, 2.2).fill({ color: SCARF, alpha: 0.9 });
}

function drawLimb(graphics: Graphics, a: PosePoint, b: PosePoint, c: PosePoint, color: number, width: number): void {
  graphics.moveTo(a.x, a.y).lineTo(b.x, b.y).lineTo(c.x, c.y).stroke({ color, width, alpha: 0.95, cap: 'round', join: 'round' });
}

function drawFoot(graphics: Graphics, view: ViewModel, foot: PosePoint, facing: -1 | 1, toeSign: -1 | 1): void {
  const toe = {
    x: foot.x + view.player.tangentX * facing * toeSign * 8,
    y: foot.y + view.player.tangentY * facing * toeSign * 8,
  };
  graphics.moveTo(foot.x, foot.y).lineTo(toe.x, toe.y).stroke({ color: SOLE, width: 5.4, alpha: 0.92, cap: 'round' });
  graphics.moveTo(foot.x, foot.y).lineTo(toe.x, toe.y).stroke({ color: LINE, width: 3.2, alpha: 0.96, cap: 'round' });
}

function drawJoint(graphics: Graphics, point: PosePoint, radius: number): void {
  graphics.circle(point.x, point.y, radius).fill({ color: SUIT_DARK, alpha: 0.82 });
  graphics.circle(point.x, point.y, Math.max(1, radius - 1)).fill({ color: LINE, alpha: 0.72 });
}

function local(view: ViewModel, tangent: number, up: number): PosePoint {
  const player = view.player;
  return {
    x: player.x + player.tangentX * tangent + player.upX * up,
    y: player.y + player.tangentY * tangent + player.upY * up,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
